import React from 'react';


/**
 * `formatAssociationName` formats an association name by removing special characters
 * and converting the name to proper case (each word capitalized).
 *
 * @param {string} name The name to format.
 * @returns {string} The formatted name.
 */
const formatAssociationName = (name) => {
  // Remove specific unwanted words/phrases and special characters, including surrounding spaces
  const unwantedWords = /\s*\b(AREA|ASSOCIATION OF REALTORS|INC|REALTORS)\b\s*/gi;
  const nameCleaned = name.replace(unwantedWords, ' ') // Replace with a single space to avoid multiple spaces
                           .replace(/[^\w\s]/gi, '')
                           .trim();

  // Convert to uppercase
  return nameCleaned.toUpperCase();
};


/**
 * `upperCase` converts a string to uppercase.
 *
 * @param {string} value The string to convert.
 * @returns {string} The string in uppercase.
 */
const upperCase = (value) => String(value).toUpperCase();

/**
 * `properCase` converts a string to proper case (each word capitalized).
 *
 * @param {string} inputString The string to convert.
 * @returns {string} The string in proper case.
 */
function properCase(inputString) {
  return inputString
    .trim()
    .toLowerCase()
    .replace(/(^|\s)\S/g, (char) => char.toUpperCase());
}

/**
 * `phoneNumber` formats a phone number string into the format (XXX) XXX-XXXX.
 *
 * @param {string} inputString The phone number string to format.
 * @returns {string} The formatted phone number string.
 */
function phoneNumber(inputString) {
  return inputString
    .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

/**
 * `calculateMembershipDuration` calculates the number of years from a given date to the current date.
 *
 * @param {string} memberSinceDateString The start date string.
 * @returns {number} The number of years from the start date to the current date.
 */
function calculateMembershipDuration(memberSinceDateString) {
  const currentDate = new Date();
  const memberSinceDate = new Date(memberSinceDateString);
  return currentDate.getFullYear() - memberSinceDate.getFullYear();
}

const extractYear = (dateString) => {
  try {
    const [, , year] = dateString.split('/'); // We only care about the year, so we use destructuring to skip the month and day parts.
    return parseInt(year);
  } catch (error) {
    throw new Error('Invalid date format. Please provide a date in the format "mm/dd/yyyy".');
  }
};


const extractDayOfWeekFromDate = (dateString) => {
  try {
    const date = new Date(dateString);

    // Get the day of the week as a string (e.g., "Monday")
    const options = { weekday: 'long' };
    const dayOfWeek = date.toLocaleDateString(undefined, options);

    return dayOfWeek;
  } catch (error) {
    throw new Error('Invalid date format. Please provide a valid date string.');
  }
};
const extractMonth = (dateString) => {
  try {
    const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; // Start with an empty string at index 0 for easier mapping.
    const [month, , ] = dateString.split('/'); // We only care about the month, so we use destructuring to skip the day and year parts.
    
    const monthNumber = parseInt(month);

    if (monthNumber < 1 || monthNumber > 12) {
      throw new Error('Invalid month number. Please ensure your date is in the format "mm/dd/yyyy" and contains a valid month.');
    }

    return monthNames[monthNumber];

  } catch (error) {
    throw new Error('Invalid date format. Please provide a date in the format "mm/dd/yyyy".');
  }
};

function generateElectionFields() {
  const currentYear = new Date().getFullYear();
  const evenYears = [];
  
  for (let year = currentYear - 1; evenYears.length < 3; year--) {
      if (year % 2 === 0) {
          evenYears.push(year);
      }
  }
  
  const electionFields = evenYears.map(year => `[VB.VF_P${year}], [VB.VF_G${year}]`).join(', ');
  return electionFields;
} 
function stringToNumeric(inputString) {
  const numericValue = parseFloat(inputString);
  if (isNaN(numericValue)) {
    return 0;
  }
  const roundedValue = Math.round(numericValue);
  return roundedValue;
}

const cleanElectionData = (field, item) => {
  const voterStatus = item && item["VB.VF_VOTER_STATUS"];
  if (!voterStatus) {
    return field; 
  }
  if (voterStatus !== "Active") {
    return 'Unmatched or Unregistered';
  }
  return field === "Unknown" ? 'Did not vote' : "Voted";
};

const formatElectionData = () => {
  const electionTurnoutFields = generateElectionFields()
    .split(', ')
    .map((field) => {
      return field.replace(/\[|\]/g, '');
    });

  const cleaningSpecifications = electionTurnoutFields.map(field => {
    return [field, field, "multi_column", cleanElectionData];
  });

  return cleaningSpecifications;
};

function getHourFromDate(dateString) {
  // Create a Date object from the provided date string
  const date = new Date(dateString);

  // Get the hour (0-23)
  const hour = date.getHours();

  // Convert the hour to 12-hour format
  const amPm = hour >= 12 ? 'PM' : 'AM';
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;

  return `${formattedHour} ${amPm}`;
}

const currentCampaign = (field, item) => {
  const startDate = new Date(field);
  const endDate = new Date(item && item['END_DATE']);
  const today = new Date();

  if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    if (endDate > today && startDate <= today) {
      return 'current';
    } else if (endDate < today && startDate < today) {
      return 'historical';
    } else if (startDate > today) {
      return 'future';
    }
  }

  // Handle the case where dates are missing or invalid
  return 'historical';
};

export {currentCampaign, getHourFromDate, extractDayOfWeekFromDate, cleanElectionData, formatElectionData, generateElectionFields, stringToNumeric, formatAssociationName, upperCase, properCase, phoneNumber, calculateMembershipDuration, extractYear, extractMonth };
